import {HttpErrorResponse} from '@angular/common/http';

/**
 * Maps a backend error response to a friendly German sentence suitable for display in the UI.
 *
 * Rules (FE-2 / TM-1000001915):
 *  - NEVER expose raw HTTP status codes, status text, or URLs.
 *  - NEVER expose raw exception messages from the server.
 *  - Recognised backend error codes (field `code` in the response body) are translated to
 *    calm, user-facing German sentences.
 *  - An unrecognised or missing code falls back to a generic German message.
 *
 * Backend error body shape: { code: string; message: string }
 */

/** Known backend error codes for the cn module. */
const CN_ERROR_MESSAGES: Record<string, string> = {
    'cn.acknowledge.alreadyAcknowledged': 'Dieser Vorgang wurde bereits quittiert.',
    'cn.activity.periodInvalid': 'Bitte eine gültige Periode angeben.',
};

/** Fallback message when the error code is not recognised or absent. */
const GENERIC_MESSAGE = 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder wenden Sie sich an den Support.';

/**
 * Extracts the backend error code from an HttpErrorResponse or a plain error object.
 * Returns null if the code cannot be determined.
 */
function extractCode(error: unknown): string | null {
    if (error instanceof HttpErrorResponse) {
        const body = error.error;
        if (body && typeof body === 'object' && typeof (body as {code?: unknown}).code === 'string') {
            return (body as {code: string}).code;
        }
        return null;
    }
    if (error && typeof error === 'object' && typeof (error as {code?: unknown}).code === 'string') {
        return (error as {code: string}).code;
    }
    return null;
}

/**
 * Maps a backend error or HttpErrorResponse to a friendly German sentence.
 *
 * @param error - An HttpErrorResponse, a plain backend error object, or any thrown value.
 * @returns A German user-facing error message. Never contains URLs, HTTP status text, or
 *          raw exception details.
 *
 * @example
 * this.apiService.acknowledgeActivity(id, {note}).subscribe({
 *   error: (err) => this.errorMessage = cnErrorMessage(err)
 * });
 */
export function cnErrorMessage(error: unknown): string {
    const code = extractCode(error);
    if (code && CN_ERROR_MESSAGES[code]) {
        return CN_ERROR_MESSAGES[code];
    }
    return GENERIC_MESSAGE;
}
